import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { HERO, PORTRAIT, STATS } from "../lib/data";
import { scrollToId } from "./Reveal";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.2 + i * 0.11, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="hero" ref={ref} className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28">
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute top-10 right-0 w-[40rem] h-[40rem] rounded-full bg-brand/20 blur-[110px]" aria-hidden />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 mb-8"
              data-testid="hero-badge"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-70 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full btn-gradient" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-ink-soft">
                Available for freelance work
              </span>
            </motion.div>

            <h1 className="font-display font-extrabold text-ink text-[2.6rem] sm:text-5xl lg:text-[4rem] leading-[1.05] tracking-tight">
              {HERO.headlineLines.map((line, i) => (
                <span key={i} className="block overflow-hidden py-0.5">
                  <motion.span className="block" variants={lineVariants} initial="hidden" animate="visible" custom={i}>
                    {line.segs.map((s, j) => (
                      <span key={j} className={s.grad ? "text-gradient" : ""}>{s.t}</span>
                    ))}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-8 text-lg text-ink-soft leading-relaxed max-w-xl font-light"
              data-testid="hero-subheadline"
            >
              {HERO.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => scrollToId("contact")}
                className="group inline-flex items-center justify-center gap-2 rounded-full btn-gradient px-7 py-4 text-base font-semibold shadow-xl shadow-brand/25 hover:opacity-90 transition-opacity"
                data-testid="hero-primary-cta"
              >
                Hire me — start a project
                <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <button
                onClick={() => scrollToId("projects")}
                className="inline-flex items-center justify-center gap-2 rounded-full glass text-ink px-7 py-4 text-base font-semibold hover:text-brand transition-colors duration-300"
                data-testid="hero-secondary-cta"
              >
                View my work
              </button>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="mt-10 space-y-3 max-w-xl"
              data-testid="hero-bullets"
            >
              {HERO.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-ink-soft">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full btn-gradient flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </motion.ul>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2.5rem] btn-gradient opacity-20 blur-2xl" aria-hidden />
              <div className="relative rounded-[2rem] overflow-hidden glass p-2">
                <motion.img
                  style={{ y: imgY }}
                  src={PORTRAIT}
                  alt="Ricky Sokhi — UI/UX Designer & Front-end Developer"
                  className="w-full h-[26rem] lg:h-[30rem] object-cover rounded-[1.6rem] scale-110"
                  data-testid="hero-portrait"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="absolute -bottom-6 -left-4 glass rounded-2xl px-5 py-4 animate-float"
                data-testid="hero-stat-card"
              >
                <div className="font-display font-extrabold text-3xl text-gradient">15+</div>
                <div className="text-xs text-ink-soft">years designing & building</div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.button
          onClick={() => scrollToId("about")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-16 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand transition-colors"
          data-testid="hero-scroll-cue"
        >
          <ArrowDown className="w-4 h-4 animate-bounce" /> Scroll to explore
        </motion.button>
      </div>
    </section>
  );
};
